import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { CardActions } from '@material-ui/core';
import WizardStepContainer from '../WizardStepContainer';
import { WizardStylesContext } from '../WizardStylesContext';
import { formInvestibleLink, navigate } from '../../../utils/marketIdPathFunctions';
import { useHistory } from 'react-router';
import { processTextAndFilesForSave } from '../../../api/files';
import { getQuillStoredState, resetEditor } from '../../TextEditors/Utilities/CoreUtils';
import _ from 'lodash';
import { OperationInProgressContext } from '../../../contexts/OperationInProgressContext/OperationInProgressContext';
import NameField, { clearNameStoredState, getNameStoredState } from '../../TextFields/NameField';
import SpinningIconLabelButton from '../../Buttons/SpinningIconLabelButton';
import { Clear, SettingsBackupRestore } from '@material-ui/icons';
import { useIntl } from 'react-intl';
import { HASH_MENTION_CHARS, useEditor } from '../../TextEditors/quillHooks';
import { realeaseInvestibleEditLock, updateInvestible } from '../../../api/investibles';
import { refreshInvestibles } from '../../../contexts/InvestibesContext/investiblesContextHelper';
import { InvestiblesContext } from '../../../contexts/InvestibesContext/InvestiblesContext';
import { DiffContext } from '../../../contexts/DiffContext/DiffContext';
import { InlineWizardContext } from '../../InlineWizard/InlineWizardContext';

function JobEditStep(props) {
  const { marketId, investible, updateFormData = () => {}, formData = {} } = props;
  const intl = useIntl();
  // T-all-2215: when hosted inline, finishing closes the inline view rather than navigating
  // (we are already on the investible page, so the navigate would be a no-op there).
  const { closeInlineWizard } = useContext(InlineWizardContext);
  const [, investiblesDispatch] = useContext(InvestiblesContext);
  const [, diffDispatch] = useContext(DiffContext);
  const [, setOperationRunning] = useContext(OperationInProgressContext);
  const history = useHistory();
  const classes = useContext(WizardStylesContext);
  const [hasValue, setHasValue] = useState(true);
  const investibleId = investible.id;
  const { description, version } = investible || {};
  const { uploadedFiles } = formData;
  const editorName = `body-editor${investibleId}`;
  const useDescription = getQuillStoredState(editorName) || description;
  const editorSpec = {
    onUpload: (files) => updateFormData({uploadedFiles: files}),
    marketId,
    mentionsAllowed: true,
    mentionDenotationChars: HASH_MENTION_CHARS,
    placeholder: intl.formatMessage({ id: 'investibleAddDescriptionDefault' }),
    value: useDescription,
    // B-all-535: the name field above owns arrival focus; both autofocusing was a timer race
    autoFocus: false
  };

  const [Editor] = useEditor(editorName, editorSpec);

  function handleSave() {
    // uploaded files on edit is the union of the new uploaded files and the old uploaded files
    const oldInvestibleUploadedFiles = investible.uploaded_files || [];
    const currentUploadedFiles = uploadedFiles || [];
    const newUploadedFiles = _.uniqBy([...currentUploadedFiles, ...oldInvestibleUploadedFiles],
      'path');
    const description = getQuillStoredState(editorName) !== null ? getQuillStoredState(editorName) :
      useDescription;
    const {
      uploadedFiles: filteredUploads,
      text: tokensRemoved,
    } = processTextAndFilesForSave(newUploadedFiles, description);
    const name = getNameStoredState(investibleId);
    const updateInfo = {
      uploadedFiles: filteredUploads,
      name,
      description: tokensRemoved,
      marketId,
      investibleId,
      version
    };
    return updateInvestible(updateInfo)
      .then((fullInvestible) => {
        setOperationRunning(false);
        resetEditor(editorName);
        clearNameStoredState(investibleId);
        refreshInvestibles(investiblesDispatch, diffDispatch, [fullInvestible]);
        if (closeInlineWizard) {
          closeInlineWizard();
        } else {
          navigate(history, formInvestibleLink(marketId, investibleId));
        }
      });
  }

  function onCancel() {
    resetEditor(editorName);
    return realeaseInvestibleEditLock(marketId, investibleId).then((newInv) => {
      setOperationRunning(false);
      refreshInvestibles(investiblesDispatch, diffDispatch, [newInv]);
      if (closeInlineWizard) {
        closeInlineWizard();
      } else {
        navigate(history, formInvestibleLink(marketId, investibleId));
      }
    });
  }

  return (
    <WizardStepContainer
      {...props}
      isLarge
    >
      <NameField id={investibleId} setHasValue={setHasValue}/>
      {Editor}
      <CardActions className={classes.actions}>
        <SpinningIconLabelButton
          icon={SettingsBackupRestore}
          disabled={!hasValue}
          whiteBackground
          onClick={handleSave}
          id="investibleUpdateButton"
        >
          {intl.formatMessage({ id: 'update'})}
        </SpinningIconLabelButton>
        <SpinningIconLabelButton onClick={onCancel} icon={Clear} id="marketAddCancelButton" whiteBackground>
          {intl.formatMessage({ id: 'marketAddCancelLabel' })}
        </SpinningIconLabelButton>
      </CardActions>
    </WizardStepContainer>
  )
}

JobEditStep.propTypes = {
  updateFormData: PropTypes.func,
  formData: PropTypes.object,
}

export default JobEditStep